// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// Function to retrieve product based on variant selection
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { OwnProps as ProductCarouselProductContextProps } from "@west/App/Components/ProductCarousel/ProductCarouselProductContext";
import { CustomAppState } from "@west/App/Store/Reducers";

/**
 * To return product variant child by selection or default variant product
 */
export const findProductVariantContext = ({
    parentProductId,
    carouselId,
    variantSelection = {},
    xProductCarouselVariantSelection,
    xProductCarouselVariantChildren,
}: {
    parentProductId: string;
    carouselId: string;
    variantSelection?: SafeDictionary<string>;
    xProductCarouselVariantSelection: CustomAppState["xProductCarouselVariantSelection"];
    xProductCarouselVariantChildren: CustomAppState["xProductCarouselVariantChildren"];
}): {
    product?: ProductModel;
    productInfo?: ProductInfo;
} => {
    const xVariantSelection = xProductCarouselVariantSelection.variantSelection[carouselId]?.[parentProductId];
    const xVariantChildren = xProductCarouselVariantChildren.variantChildren[carouselId]?.[parentProductId];
    /**
     * If there are more than one variant traint,the product displayed will depend on last Variant Selection changed
     */
    const variantTraitId = xVariantSelection?.variantTraitId;
    const traitValueId = variantTraitId ? variantSelection?.[variantTraitId] : Object.values(variantSelection)?.[0];

    const product = xVariantChildren?.products?.find(product => {
        return !!product.childTraitValues?.find(childTrait => childTrait.id === traitValueId);
    });

    return {
        product,
        productInfo: product ? xVariantChildren?.productInfosByProductId[product.id] : undefined,
    };
};

/**
 * @param parentProduct
 * @param parentProductInfo
 * @param parentProductId
 * @param xProductCarouselVariantSelection
 * @param xProductCarouselVariantChildren
 *
 * For update parentProduct and replace attrs to variantProduct attrs
 */
const createProductVariantContext = ({
    parentProduct: parentProductProps,
    parentProductInfo,
    parentProductId,
    carouselId,
    xProductCarouselVariantSelection,
    xProductCarouselVariantChildren,
}: {
    parentProduct: ProductModel;
    parentProductInfo?: ProductInfo;
    parentProductId: string;
    carouselId: string;
    xProductCarouselVariantSelection: CustomAppState["xProductCarouselVariantSelection"];
    xProductCarouselVariantChildren: CustomAppState["xProductCarouselVariantChildren"];
}): {
    product: ProductModel;
    productInfo?: ProductInfo;
} => {
    // To solve read object only TS issue
    const parentProduct: typeof parentProductProps = { ...parentProductProps };

    const variantSelection = xProductCarouselVariantSelection.variantSelection[carouselId]?.[parentProductId] || {};
    const { product: productVariant, productInfo: productVariantInfo } = findProductVariantContext({
        variantSelection,
        parentProductId,
        carouselId,
        xProductCarouselVariantSelection,
        xProductCarouselVariantChildren,
    });

    if (!productVariant || !productVariantInfo) {
        return {
            product: parentProduct,
            productInfo: parentProductInfo,
        };
    }

    type ProductKeys = keyof ProductModel;
    const excludeKeys: ProductKeys[] = ["properties", "variantTraits"];

    /**
     * Update all product attr values
     */
    Object.keys(parentProduct).forEach(key => {
        const productKey = key as ProductKeys;

        if (!(key in productVariant) || excludeKeys.includes(productKey)) {
            return;
        }

        (parentProduct as any)[productKey] = productVariant[productKey]!;
    });

    /**
     * These attrs aren't exist in parentProduct (not variant product), so we pass attrs manually
     */
    parentProduct.childTraitValues = productVariant.childTraitValues;
    parentProduct.badges = productVariant.badges;
    parentProduct.unitOfMeasures = productVariant.unitOfMeasures;
    parentProduct.properties = {
        ...parentProduct.properties,
        ...productVariant.properties,
    };

    return {
        product: parentProduct,
        productInfo: productVariantInfo,
    };
};

/**
 * We call the method in MapStateToProps
 * @param state
 * @param ownProps
 */
export const provideProductCarouselContext = (state: CustomAppState, ownProps: ProductCarouselProductContextProps) => {
    const product = ownProps.product;
    const parentProductId = product.properties.styleParentId || product.defaultChildProductId;
    /**
     * VariantProduct means a product that has variants
     */
    const isVariantProduct = !!parentProductId;

    const productInfo = getProductInfoFromList(state, ownProps.carouselId, product.id);

    if (!isVariantProduct) {
        return {
            product,
            productInfo,
        };
    }

    const { product: variantProduct, productInfo: variantProductInfo } = createProductVariantContext({
        parentProduct: product,
        parentProductInfo: productInfo,
        parentProductId,
        carouselId: ownProps.carouselId,
        xProductCarouselVariantSelection: state.xProductCarouselVariantSelection,
        xProductCarouselVariantChildren: state.xProductCarouselVariantChildren,
    });

    return {
        product: variantProduct,
        productInfo: variantProductInfo,
        parentProductId,
        isOriginalProduct: isVariantProduct && !!variantProduct.defaultChildProductId,
    };
};
